import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, Play, Lock } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import {
  getToolkitItems,
  addToolkitItem,
  deleteToolkitItem,
  TOOLKIT_TYPES,
  FREE_TOOLKIT_LIMIT,
} from "@/lib/toolkit";
import { usePremium } from "@/lib/premium";
import { track } from "@/lib/analytics";

export default function Toolkit() {
  const nav = useNavigate();
  const { premium } = usePremium();
  const [items, setItems] = useState(() => getToolkitItems());
  const [composing, setComposing] = useState(false);
  const [type, setType] = useState("strategy");
  const [label, setLabel] = useState("");
  const [detail, setDetail] = useState("");

  const atLimit = !premium && items.length >= FREE_TOOLKIT_LIMIT;
  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => {
      (g[i.type] = g[i.type] || []).push(i);
    });
    return g;
  }, [items]);

  const handleAdd = () => {
    if (!label.trim()) return;
    const res = addToolkitItem({ type, label, detail }, { premium });
    if (res.ok) {
      setItems(getToolkitItems());
      setLabel("");
      setDetail("");
      setComposing(false);
      toast("Kept in your Toolkit.");
    } else if (res.reason === "limit") {
      toast("Your Toolkit is full for now.", {
        description: "Free includes 8 saved items. Premium removes the limit.",
      });
    }
  };

  const handleDelete = (id) => {
    deleteToolkitItem(id);
    setItems(getToolkitItems());
  };

  return (
    <PageContainer testid="toolkit-page" className="pt-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1
          className="text-[30px] leading-tight"
          style={{ color: "var(--un-text-1)", fontFamily: "'Lora', serif", fontWeight: 500 }}
        >
          Your Toolkit
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
          A private collection of what works for you — exercises, strategies,
          reasons to pause, people to reach.
        </p>
      </motion.div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-[12px]" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
          {items.length} kept{!premium && ` · ${FREE_TOOLKIT_LIMIT} free`}
        </span>
        <button
          data-testid="toolkit-add-btn"
          onClick={() => (atLimit ? nav("/premium?source=toolkit") : setComposing((v) => !v))}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium un-press"
          style={{ background: "var(--un-plum)", color: "#FDFBF7", fontFamily: "Manrope, sans-serif" }}
        >
          {atLimit ? <Lock size={13} /> : <Plus size={14} />} Add
        </button>
      </div>

      {composing && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 p-4 rounded-3xl overflow-hidden"
          style={{ background: "var(--un-bg-2)" }}
        >
          <div className="flex flex-wrap gap-2 mb-3">
            {TOOLKIT_TYPES.filter((t) => t.id !== "exercise").map((t) => (
              <button
                key={t.id}
                data-testid={`toolkit-type-${t.id}`}
                onClick={() => setType(t.id)}
                className="px-3 py-1.5 rounded-full text-[12px] un-press"
                style={{
                  background: type === t.id ? "var(--un-plum)" : "rgba(91,57,84,0.08)",
                  color: type === t.id ? "#FDFBF7" : "var(--un-text-2)",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            data-testid="toolkit-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={TOOLKIT_TYPES.find((t) => t.id === type)?.hint}
            className="w-full outline-none rounded-2xl p-3 text-[15px] mb-2"
            style={{
              background: "rgba(253,251,247,0.8)",
              color: "var(--un-text-1)",
              border: "1px solid rgba(74,32,64,0.10)",
              fontFamily: "DM Sans, sans-serif",
            }}
          />
          <textarea
            data-testid="toolkit-detail-input"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="A little more, if it helps (optional)"
            rows={2}
            className="w-full outline-none rounded-2xl p-3 text-[14px] resize-none"
            style={{
              background: "rgba(253,251,247,0.8)",
              color: "var(--un-text-1)",
              border: "1px solid rgba(74,32,64,0.10)",
              fontFamily: "DM Sans, sans-serif",
            }}
          />
          <button
            data-testid="toolkit-save-btn"
            onClick={handleAdd}
            disabled={!label.trim()}
            className="mt-2 w-full py-3 rounded-full text-[14px] font-medium un-press disabled:opacity-50"
            style={{ background: "var(--un-plum)", color: "#FDFBF7", fontFamily: "Manrope, sans-serif" }}
          >
            Keep this
          </button>
        </motion.div>
      )}

      {items.length === 0 && !composing ? (
        <div
          data-testid="toolkit-empty-state"
          className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: "var(--un-bg-2)" }}
        >
          <p className="text-[15px]" style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}>
            Nothing kept yet.
          </p>
          <p className="text-[13px] mt-1.5 leading-relaxed" style={{ color: "var(--un-text-3)" }}>
            Finish a reset and tap "Keep in my Toolkit" — or add a strategy,
            a reason to pause, or a person you can reach.
          </p>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-6 mb-10">
          {TOOLKIT_TYPES.filter((t) => grouped[t.id]?.length).map((t) => (
            <div key={t.id}>
              <p
                className="text-[10px] uppercase tracking-[0.28em] mb-2.5"
                style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
              >
                {t.label}
              </p>
              <div className="flex flex-col gap-2.5">
                {grouped[t.id].map((item) => (
                  <div
                    key={item.id}
                    data-testid={`toolkit-item-${item.id}`}
                    className="p-4 rounded-3xl flex items-center gap-3"
                    style={{ background: "var(--un-bg-2)", boxShadow: "0 4px 14px rgba(74,45,72,0.05)" }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>
                        {item.label}
                      </p>
                      {item.detail && (
                        <p className="text-[12px] mt-0.5" style={{ color: "var(--un-text-3)" }}>
                          {item.detail}
                        </p>
                      )}
                    </div>
                    {item.type === "exercise" && item.refId && (
                      <button
                        data-testid={`toolkit-play-${item.id}`}
                        onClick={() => nav(`/reset?play=${item.refId}`)}
                        aria-label="Start this exercise"
                        className="p-2.5 rounded-full un-press"
                        style={{ background: "rgba(196,161,106,0.18)", color: "var(--un-plum)" }}
                      >
                        <Play size={14} />
                      </button>
                    )}
                    <button
                      data-testid={`toolkit-delete-${item.id}`}
                      onClick={() => handleDelete(item.id)}
                      aria-label="Remove"
                      className="p-2.5 rounded-full un-press"
                      style={{ color: "var(--un-text-3)" }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!premium && (
            <button
              data-testid="toolkit-premium-hint"
              onClick={() => {
                track("paywall_viewed", { source: "toolkit" });
                nav("/premium?source=toolkit");
              }}
              className="un-press p-4 rounded-3xl text-left"
              style={{ background: "linear-gradient(135deg, rgba(196,161,106,0.14) 0%, rgba(91,57,84,0.08) 100%)" }}
            >
              <p className="text-[13px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>
                Unlimited Toolkit
              </p>
              <p className="text-[12px] mt-0.5" style={{ color: "var(--un-text-3)" }}>
                Premium keeps space for everything that works for you.
              </p>
            </button>
          )}
        </div>
      )}
    </PageContainer>
  );
}
